import React, { useEffect, useMemo, useRef, useState } from "react";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

/** Same-origin base path for downloadable files */
const DOWNLOAD_BASE = "/download/";

/**
 * Token verification API endpoint (Apache proxies /backend2/* → Node:8082).
 * If you ever expose Node directly, you could set a full URL here instead.
 */
const VERIFY_ENDPOINT = "/backend2/api/download/verify";

const SESSION_TOKEN_KEY = "gista_download_token";

/* ---- helpers ---- */
function ensureTrailingSlash(u) {
  return u.endsWith("/") ? u : u + "/";
}
function joinUrl(baseAbs, rel) {
  return new URL(rel, baseAbs).toString();
}
function sameOriginRelative(urlStr) {
  const base = new URL(DOWNLOAD_BASE, window.location.origin);
  const u = new URL(urlStr, base);
  let rel = u.pathname;
  if (rel.startsWith(base.pathname)) rel = rel.slice(base.pathname.length);
  if (rel.startsWith("/")) rel = rel.slice(1);
  return rel;
}

/** token: <expiry>:<rand>:<sigHex> */
function parseTokenExpiry(token) {
  const parts = String(token || "").trim().split(":");
  if (parts.length < 3) return null;
  const expiry = Number(parts[0]);
  if (!Number.isFinite(expiry) || expiry <= 0) return null;
  return expiry;
}
function isExpired(expiryUnixSeconds) {
  if (!expiryUnixSeconds) return true;
  const now = Math.floor(Date.now() / 1000);
  return now > expiryUnixSeconds;
}

function extractTokenFromInput(raw) {
  const s = String(raw || "").trim();

  // If the user pasted a full URL with ?token=...
  if (s.startsWith("http://") || s.startsWith("https://")) {
    try {
      const u = new URL(s);
      const t = u.searchParams.get("token");
      if (t) return t;
    } catch {
      // fallthrough
    }
  }

  // If the user pasted some text that contains token=...
  const m = s.match(/(?:\?|&|\b)token=([^&\s]+)/i);
  if (m?.[1]) return decodeURIComponent(m[1]);

  // Otherwise assume they pasted the raw token
  return s;
}

/** Apache autoindex HTML → items */
function parseAutoIndex(htmlText, listUrlAbs) {
  const doc = new DOMParser().parseFromString(htmlText, "text/html");
  const anchors = Array.from(doc.querySelectorAll("a"));
  const items = [];

  for (const a of anchors) {
    const text = (a.textContent || "").trim();
    const href = a.getAttribute("href") || "";
    if (!href) continue;

    const low = text.toLowerCase();
    if (low.includes("parent directory")) continue;
    if (href.startsWith("?")) continue;
    if (href.includes("/icons/")) continue;

    const abs = joinUrl(listUrlAbs, href);
    if (
      !abs.includes(
        ensureTrailingSlash(new URL(DOWNLOAD_BASE, window.location.origin).pathname)
      )
    )
      continue;

    const nameFromHref = decodeURIComponent(
      href.split("/").filter(Boolean).pop() || text
    );
    const name = (nameFromHref || text || "").trim();
    if (!name) continue;

    const isDir = href.endsWith("/");
    items.push({
      name,
      url: abs,
      rel: sameOriginRelative(abs),
      type: isDir ? "folder" : "file",
    });
  }

  items.sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    return a.name.localeCompare(b.name, "hu", { sensitivity: "base" });
  });

  return items;
}

/** Row */
function Row({ depth, entry, onToggleFolder, baseAbs, canDownload }) {
  const isFolder = entry.type === "folder";
  const isFile = entry.type === "file";
  const href = isFile ? joinUrl(baseAbs, entry.rel) : undefined;

  return (
    <div
      onClick={isFolder ? () => onToggleFolder(entry) : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        paddingLeft: 8 + depth * 16,
        cursor: isFolder ? "pointer" : "default",
        userSelect: "none",
        opacity: canDownload ? 1 : 0.55,
      }}
      title={entry.name}
    >
      <span
        style={{
          flex: "0 0 auto",
          width: 20,
          display: "inline-flex",
          justifyContent: "center",
        }}
      >
        {isFolder ? (
          entry.__open ? (
            <FolderOpenIcon fontSize="small" />
          ) : (
            <FolderIcon fontSize="small" />
          )
        ) : (
          <InsertDriveFileIcon fontSize="small" />
        )}
      </span>

      <div
        style={{
          flex: "1 1 auto",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          fontStyle: isFolder ? "italic" : "normal",
          fontWeight: isFile ? 600 : 400,
        }}
      >
        {entry.name}
      </div>

      {isFile && (
        <a
          href={canDownload ? href : undefined}
          download={canDownload ? true : undefined}
          style={{
            textDecoration: "none",
            pointerEvents: canDownload ? "auto" : "none",
          }}
          onClick={(e) => e.stopPropagation()}
          title={canDownload ? "Download" : "Token missing/expired — downloads disabled"}
        >
          <CloudDownloadIcon fontSize="small" />
        </a>
      )}
    </div>
  );
}

/** Panel */
export default function DownloadsPanel() {
  const [open, setOpen] = useState(false);

  const baseAbs = useMemo(
    () => new URL(DOWNLOAD_BASE, window.location.origin).toString(),
    []
  );

  // auth state
  const [authorized, setAuthorized] = useState(false);
  const [expiry, setExpiry] = useState(null);
  const [tokenInput, setTokenInput] = useState("");
  const [authError, setAuthError] = useState("");
  const verifyingRef = useRef(false);

  const canDownload = authorized && expiry && !isExpired(expiry);

  // cache & state
  const [cache, setCache] = useState(() => new Map()); // relPath -> { items, fetchedUrl }
  const [expanded, setExpanded] = useState(() => new Set()); // open folders
  const [loadingSet, setLoadingSet] = useState(() => new Set());
  const [errorByPath, setErrorByPath] = useState(() => new Map());

  const rootRel = "";

  const verifyTokenSilent = async (raw) => {
    // remove whitespace (email line breaks etc.)
    const t = extractTokenFromInput(raw).replace(/\s+/g, "");
    if (!t) return false;

    const exp = parseTokenExpiry(t);
    if (!exp || isExpired(exp)) return false;

    if (verifyingRef.current) return false;
    verifyingRef.current = true;

    try {
      const res = await fetch(
        `${VERIFY_ENDPOINT}?token=${encodeURIComponent(t)}`,
        { credentials: "same-origin" }
      );
      if (!res.ok) return false;

      setAuthorized(true);
      setExpiry(exp);
      setAuthError("");
      sessionStorage.setItem(SESSION_TOKEN_KEY, t);
      return true;
    } catch {
      return false;
    } finally {
      verifyingRef.current = false;
    }
  };

  // On open: URL token → session token
  useEffect(() => {
    if (!open) return;

    (async () => {
      setAuthError("");

      const params = new URLSearchParams(window.location.search);
      const urlTok = (params.get("token") || "").trim();
      if (urlTok) {
        const ok = await verifyTokenSilent(urlTok);
        if (!ok) setAuthError("The download token is invalid or expired.");
        return;
      }

      const sessTok = (sessionStorage.getItem(SESSION_TOKEN_KEY) || "").trim();
      if (sessTok) {
        const ok = await verifyTokenSilent(sessTok);
        if (!ok) {
          sessionStorage.removeItem(SESSION_TOKEN_KEY);
          setAuthorized(false);
          setExpiry(null);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Realtime expiry watchdog
  useEffect(() => {
    if (!authorized || !expiry) return;

    const tick = () => {
      if (isExpired(expiry)) {
        setAuthorized(false);
        setExpiry(null);
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
      }
    };

    tick();
    const id = window.setInterval(tick, 10_000);
    return () => window.clearInterval(id);
  }, [authorized, expiry]);

  /** List a relative path under /download/ (if not cached) */
  const ensureListed = async (relPath) => {
    const rel = relPath || "";
    if (cache.has(rel)) return;

    const listUrlAbs = joinUrl(baseAbs, rel);
    setLoadingSet((s) => new Set(s).add(rel));

    try {
      const res = await fetch(listUrlAbs, { credentials: "same-origin" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const items = parseAutoIndex(html, listUrlAbs);

      setCache((prev) => {
        const m = new Map(prev);
        m.set(rel, { items, fetchedUrl: listUrlAbs });
        return m;
      });

      setErrorByPath((prev) => {
        const m = new Map(prev);
        m.delete(rel);
        return m;
      });
    } catch (e) {
      setErrorByPath((prev) => {
        const m = new Map(prev);
        m.set(rel, String(e));
        return m;
      });
    } finally {
      setLoadingSet((s) => {
        const n = new Set(s);
        n.delete(rel);
        return n;
      });
    }
  };

  // On open, load root (only if authorized)
  useEffect(() => {
    if (open && canDownload) ensureListed(rootRel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canDownload]);

  const toggleFolder = async (entry) => {
    const rel = ensureTrailingSlash(entry.rel);
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(rel)) n.delete(rel);
      else n.add(rel);
      return n;
    });
    if (!cache.has(rel)) await ensureListed(rel);
  };

  const renderLevel = (relPath, depth = 0) => {
    const node = cache.get(relPath);
    const isLoading = loadingSet.has(relPath);
    const err = errorByPath.get(relPath);

    if (isLoading && !node) {
      return (
        <div
          key={`loading-${relPath}`}
          style={{ padding: 12, display: "flex", alignItems: "center", gap: 8 }}
        >
          <CircularProgress size={18} /> Loading…
        </div>
      );
    }
    if (err && !node) {
      return (
        <div key={`err-${relPath}`} style={{ padding: 12, color: "#b00020" }}>
          Error while listing: {err}
        </div>
      );
    }
    if (!node) return null;

    return node.items.map((it) => {
      if (it.type === "folder") {
        const relFolder = ensureTrailingSlash(it.rel);
        const openFolder = expanded.has(relFolder);
        return (
          <React.Fragment key={it.rel}>
            <Row
              depth={depth}
              entry={{ ...it, __open: openFolder }}
              onToggleFolder={toggleFolder}
              baseAbs={baseAbs}
              canDownload={canDownload}
            />
            {openFolder && renderLevel(relFolder, depth + 1)}
          </React.Fragment>
        );
      }
      return (
        <Row
          key={it.rel}
          depth={depth}
          entry={it}
          onToggleFolder={() => {}}
          baseAbs={baseAbs}
          canDownload={canDownload}
        />
      );
    });
  };

  const handleSubmitToken = async (e) => {
    e.preventDefault();
    const ok = await verifyTokenSilent(tokenInput);
    if (!ok) {
      setAuthorized(false);
      setExpiry(null);
      setAuthError("The download token is invalid or expired.");
    }
  };

  return (
    <div>
      {!open && (
        <Tooltip title="Open downloads">
          <IconButton
            style={{ color: "gray" }}
            onClick={() => setOpen(true)}
            aria-label="open-downloads"
          >
            <CloudDownloadIcon />
          </IconButton>
        </Tooltip>
      )}

      <Paper
        elevation={10}
        style={{
          padding: 10,
          minWidth: 260,
          maxWidth: "calc(100vw - 35px)",
          maxHeight: "calc(100vh - 150px)",
          display: open ? "block" : "none",
          paddingLeft: 16,
          paddingRight: 16,
          overflowX: "hidden",
          overflowY: "auto",
          borderRadius: 10,
          backgroundColor: "#f0f0f0",
        }}
      >
        <IconButton
          style={{ color: "gray", float: "right", marginRight: -8, marginTop: -8 }}
          onClick={() => setOpen(false)}
          aria-label="close-downloads"
        >
          <CloseIcon style={{ width: 20 }} />
        </IconButton>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "6px 8px",
            borderBottom: "1px solid #eee",
          }}
        >
          <h2>Downloads</h2>
        </div>

        {!canDownload && (
          <div style={{ paddingTop: 10, paddingBottom: 10 }}>
            <div>
              A 48-hour download token is required.
              For download possibilities please contact <a style={{textDecoration:'none', fontWeight:'bold', color:'#34A997'}} href="mailto:demetergg@gmail.com">Gábor Demeter</a> at <a style={{textDecoration:'none', fontWeight:'bold', color:'#34A997'}} href="mailto:demetergg@gmail.com">demetergg@gmail.com</a>
            </div>

            <form onSubmit={handleSubmitToken} style={{ marginTop: 14, fontSize: 16 }}>
              <div style={{ marginBottom: 4 }}>
                Download token (or link):
              </div>
              <input
                type="text"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                style={{
                  width: "100%",
                  padding: 6,
                  borderRadius: 6,
                  border: "1px solid #bbb",
                  boxSizing: "border-box",
                  fontSize: 16,
                }}
                placeholder="Paste token or full URL here"
              />
              <button
                type="submit"
                style={{
                  marginTop: 8,
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "1px solid #444",
                  background: "#222",
                  color: "white",
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                Enter
              </button>
            </form>

            {authError && (
              <div style={{ color: "#b00020", marginTop: 8 }}>
                {authError}
              </div>
            )}
          </div>
        )}

        {canDownload && (
          <div style={{ paddingTop: 6, paddingBottom: 10 }}>
            {renderLevel(rootRel)}
          </div>
        )}

        {/* --- CC licence + required citation --- */}
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #ddd" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <a
              href="https://creativecommons.org/licenses/by-nc-sa/4.0/"
              target="_blank"
              rel="noreferrer"
              title="Creative Commons Attribution-NonCommercial-ShareAlike 4.0 International"
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <img
                src="https://licensebuttons.net/l/by-nc-sa/4.0/88x31.png"
                alt="CC BY-NC-SA 4.0"
                style={{ height: 31 }}
              />
            </a>
            <div style={{ fontSize: 12.5, lineHeight: 1.35, maxWidth: 720 }}>
              All materials are available under the <strong>Creative Commons CC BY-NC-SA 4.0</strong> licence for{" "}
              <strong>non-commercial use only</strong>.
            </div>
          </div>

          <div style={{ fontSize: 12.5, marginTop: 10 }}>
            <strong>Required citation:</strong>
            <div
              style={{
                marginTop: 6,
                padding: 10,
                borderRadius: 8,
                background: "#fff",
                border: "1px solid #e0e0e0",
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              }}
            >
              GIStatistica Croatica. Gábor Demeter et al. ELKH PoC 2021; HAS RCH Lendület Ten Generations; NKFIH OTKA K 111 766; HUNRENTECH 2024-1.
              Licensed under CC BY-NC-SA 4.0.
            </div>
          </div>
        </div>
      </Paper>
    </div>
  );
}
